import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Message } from 'semantic-ui-react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import cx from 'classnames';
import { defineMessages, useIntl } from 'react-intl';
import Body from '@kitconcept/volto-slider-block/components/Body';
import withBlockExtensions from '@plone/volto/helpers/Extensions/withBlockExtensions';
import {
  DotButton,
  NextButton,
  PrevButton,
} from '@kitconcept/volto-slider-block/components/DotsAndArrows';
import teaserTemplate from '@kitconcept/volto-slider-block/icons/teaser-template.svg';

const messages = defineMessages({
  PleaseChooseContent: {
    id: 'Please choose an existing content as source for this element',
    defaultMessage:
      'Please choose an existing content as source for this element',
  },
});

const safe = (s) => (typeof s === 'string' ? s : '');

const SlideCopyright = ({ text }) => {
  const [open, setOpen] = React.useState(false);

  if (!text) return null;

  const toggle = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setOpen((o) => !o);
  };

  return (
    <div
      className={cx('fcmh-slider__copyright', { 'is-open': open })}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="fcmh-slider__copyright-icon"
        aria-label="Bildnachweis anzeigen"
        aria-expanded={open}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
            setOpen(false);
          }
        }}
      >
        ©
      </button>

      <div className="fcmh-slider__copyright-tooltip" role="tooltip">
        <span className="fcmh-slider__copyright-text">{text}</span>
      </div>
    </div>
  );
};

SlideCopyright.propTypes = {
  text: PropTypes.string,
};

const SliderView = (props) => {
  const {
    className,
    data,
    isEditMode = false,
    block,
    openObjectBrowser,
    onChangeBlock,
    slideIndex,
    setSlideIndex,
  } = props;
  const intl = useIntl();

  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  const autoplay =
    data.autoplayEnabled !== undefined ? data.autoplayEnabled : false;
  const autoplayOptions = {
    delay: data.autoplayDelay,
    jump: data.autoplayJump,
  };
  const plugins = isEditMode ? [] : autoplay ? [Autoplay(autoplayOptions)] : [];

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, plugins);

  const scrollPrev = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollPrev();
      setSlideIndex && setSlideIndex(selectedIndex - 1);
    }
  }, [emblaApi, selectedIndex, setSlideIndex]);

  const scrollNext = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollNext();
      setSlideIndex && setSlideIndex(selectedIndex + 1);
    }
  }, [emblaApi, selectedIndex, setSlideIndex]);

  const scrollTo = useCallback(
    (index) => {
      if (emblaApi) {
        emblaApi.scrollTo(index);
        setSlideIndex && setSlideIndex(index);
      }
    },
    [emblaApi, setSlideIndex],
  );

  const onInit = useCallback((api) => {
    setScrollSnaps(api.scrollSnapList());
  }, []);

  const onSelect = useCallback((api) => {
    setSelectedIndex(api.selectedScrollSnap());
    setPrevBtnDisabled(!api.canScrollPrev());
    setNextBtnDisabled(!api.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on('reInit', onInit);
    emblaApi.on('reInit', onSelect);
    emblaApi.on('select', onSelect);
  }, [emblaApi, onInit, onSelect]);

  useEffect(() => {
    if (isEditMode) {
      scrollTo(slideIndex);
    }
  }, [slideIndex, scrollTo, isEditMode]);

  const sliderContainerWidth = emblaApi
    ?.rootNode()
    ?.getBoundingClientRect().width;

  // INTERAKTIV CUSTOM START
  const title =
    data.sectionTitle || data?.styles?.sectionTitle || data?.data?.sectionTitle;

  const renderSectionHeading = (titleValue) =>
    titleValue ? (
      <div className="slider-section-heading">
        <h2 className="title">{titleValue}</h2>
      </div>
    ) : null;
  // INTERAKTIV CUSTOM END

  return (
    <>
      <div
        className={cx('block slider', data.variation || 'default', className)}
        style={{ '--slider-container-width': `${sliderContainerWidth || 0}px` }}
      >
        {renderSectionHeading(title)}

        {(data.slides?.length === 0 || !data.slides) && isEditMode && (
          <Message>
            <div className="teaser-item default">
              <img src={teaserTemplate} alt="" />
              <p>{intl.formatMessage(messages.PleaseChooseContent)}</p>
            </div>
          </Message>
        )}

        {data.slides?.length > 0 && (
          <>
            <div className="slider-wrapper">
              {!data.hideArrows && data.slides?.length > 1 && (
                <>
                  <PrevButton onClick={scrollPrev} disabled={prevBtnDisabled} />
                  <NextButton onClick={scrollNext} disabled={nextBtnDisabled} />
                </>
              )}

              <div className="slider-viewport" ref={emblaRef}>
                <div className="slider-container">
                  {data.slides.map((item, index) => {
                    const copyrightText = safe(item?.copyrightText).trim();

                    return (
                      <div key={item['@id'] || index} className="slider-slide">
                        <Body
                          {...props}
                          data={item}
                          isEditMode={isEditMode}
                          dataBlock={data}
                          index={index}
                          block={block}
                          openObjectBrowser={openObjectBrowser}
                          onChangeBlock={onChangeBlock}
                          isActive={selectedIndex === index}
                        />

                        <SlideCopyright text={copyrightText} />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {data.slides?.length > 1 && (
              <div className="slider-dots">
                {scrollSnaps.map((_, index) => (
                  <DotButton
                    key={index}
                    index={index}
                    onClick={() => scrollTo(index)}
                    className={'slider-dot'.concat(
                      index === selectedIndex ? ' slider-dot--selected' : '',
                    )}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default withBlockExtensions(SliderView);
